import { faTrash as regTrash } from '@fortawesome/pro-regular-svg-icons';
import { faTrash as solidTrash } from '@fortawesome/pro-solid-svg-icons';
import { ActionModal } from '@kcftech/design-system';
import { ICorporationResponse, ITimeRangeResponse } from '@kcftech/prometheus-client';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { Table } from 'components/data_table/Table';
import { TableColumn } from 'components/data_table/types';
import {
    Dropdown,
    DropdownItemRenderer,
    DropdownLabelRenderer,
} from 'components/dropdown/Dropdown';
import { SearchInput } from 'components/search/SearchInput';
import { useCorporationTimeRangeDataSource } from 'custom_hooks/data_hooks/data_source/corporationTimeRangesDataSource';
import { useInfiniteTimeRangeDataSource } from 'custom_hooks/data_hooks/data_source/TimeRangesDataSource';
import { useDrainDataSource } from 'custom_hooks/data_hooks/data_source/useDrainDataSource';
import { useDebounce } from 'custom_hooks/useDebounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { tokens } from 'styles/tokens';
import { getId } from 'utils/data';

const corporationTimeRangeQueryKey = 'corp-time-range-table';

const renderItem: DropdownItemRenderer<ITimeRangeResponse> = ({ item }) =>
    `${item.Name} (${item.TimeInSeconds} seconds)`;

const renderLabel: DropdownLabelRenderer<ITimeRangeResponse> = ({ value }) =>
    value?.Name ?? 'Add a time range';

export type CorporationTimeRangeModalProps = Readonly<{
    corporation: ICorporationResponse;
    onClose: () => void;
    onConfirm: (timeRanges: ITimeRangeResponse[]) => void;
}>;

export function CorporationTimeRangeModal({
    corporation,
    onClose,
    onConfirm,
}: CorporationTimeRangeModalProps) {
    const [search, setSearch] = useState('');
    const debounceSearch = useDebounce(search, 250);
    const {
        results: currentAssignedTimeRanges,
        isLoading,
    } = useCorporationTimeRangeDataSource({
        name: corporationTimeRangeQueryKey,
        defaultFilter: {
            corporationId: corporation.Id,
            nameSearch: undefined,
        },
        defaultSort: [
            {
                field: 'TimeInSeconds',
                dir: 'asc',
            },
        ],
        pageLimit: 100,
    });

    const { results: timeRanges } = useDrainDataSource(
        useInfiniteTimeRangeDataSource({
            name: 'time-ranges',
            defaultFilter: {
                nameSearch: undefined,
            },
        }),
    );

    useEffect(() => {
        setPendingChanges(currentAssignedTimeRanges ?? []);
    }, [currentAssignedTimeRanges]);
    const [pendingChanges, setPendingChanges] = useState(currentAssignedTimeRanges ?? []);
    const onDeleteClick = useCallback(
        (timeRange: ITimeRangeResponse) => {
            setPendingChanges([
                ...pendingChanges.filter((row) => row.Id !== timeRange.Id),
            ]);
        },
        [pendingChanges],
    );

    const displayedTimeRanges = useMemo(() => {
        if (debounceSearch || !isLoading) {
            return pendingChanges.filter(
                (timeRange) =>
                    timeRange.Name.includes(debounceSearch) ||
                    timeRange.Description.includes(debounceSearch) ||
                    timeRange.TimeInSeconds.toString().includes(debounceSearch),
            );
        }
        return pendingChanges;
    }, [pendingChanges, debounceSearch, isLoading]);

    const unassignedTimeRanges = useMemo(() => {
        if (timeRanges) {
            const pendingIds = pendingChanges.map((t) => t.Id);
            return timeRanges.filter((timeRange) => !pendingIds.includes(timeRange.Id));
        }
        return [];
    }, [pendingChanges, timeRanges]);

    const columns: TableColumn<ITimeRangeResponse>[] = useMemo(
        () => [
            {
                id: 'Name',
                header: () => 'Button Text',
                value: ({ rowData }) => rowData.Name,
                primary: true,
            },
            {
                id: 'TimeInSeconds',
                header: () => 'Time',
                value: ({ rowData }) => rowData.TimeInSeconds,
                sortOn: 'TimeInSeconds',
            },
            {
                id: 'Description',
                header: () => 'Tooltip Description',
                value: ({ rowData }) => rowData.Description,
            },
            {
                id: 'Action',
                header: () => 'Action',
                value: ({ rowData }) => (
                    <HoverIconButton
                        icon={regTrash}
                        hoverIcon={solidTrash}
                        color={tokens.text.secondary}
                        onClick={() => onDeleteClick(rowData)}
                    />
                ),
            },
        ],
        [onDeleteClick],
    );

    return (
        <ActionModal
            title={`Manage ${corporation.Name}'s Time Ranges`}
            actionButtonText="Save"
            closeButtonText="Cancel"
            height={750}
            width={1300}
            onClose={onClose}
            onAction={() => {
                onConfirm(pendingChanges);
            }}
        >
            <div className="scrollable-y">
                <span className="flex justify-between pb-10">
                    <SearchInput
                        name="search"
                        value={search}
                        placeholder="Search"
                        onChange={(event) => setSearch(event.target.value)}
                    />
                    <div>
                        <Dropdown<ITimeRangeResponse>
                            data={unassignedTimeRanges}
                            dataKey={getId}
                            value={
                                pendingChanges.length > 1 ? pendingChanges[0] : undefined
                            }
                            renderItem={renderItem}
                            renderLabel={renderLabel}
                            onChange={(value) => {
                                setPendingChanges([value, ...pendingChanges]);
                            }}
                            popup={{ width: '30rem' }}
                        />
                    </div>
                </span>
                {!isLoading && (
                    <Table
                        tableStyle="data"
                        data={displayedTimeRanges}
                        rowKey={(rowData) => rowData.Id}
                        columns={columns}
                    />
                )}
            </div>
        </ActionModal>
    );
}
